import {
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
  IAddressActionRulesMap,
  IAddressLineActionRules,
  IAddressLineActions,
  IBasketAction,
} from '@epicuro-next/data/baskets';
import * as basketDto from '@epicuro-next/data/baskets-dto';
import { UserRole } from '@epicuro-next/data/users';
import { Filter } from '@epicuro-next/models/filters';
import {
  getValueFromConfigState,
  selectConfigurationsState,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  ensureArray,
  getDiscountPercentageInCorrectRange,
  isString,
  mergeDeep,
  Subset,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BasketRowsGroupType } from 'graphql-api/baskets-api-interfaces';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import {
  basketColumnsNotConfiguredValue,
  basketConfigurationsDefaultValues,
  CBasketConfigurations,
  defaultManualDiscountsRules,
} from '../../configurations';
import { BasketColumnsConfiguration } from '../../models/basket-columns-configuration';
import { ManualDiscountsRules, MaxDiscountPercentageValue } from '../../types';

export const selectAlternativeAddressTitleTypeRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.AlternativeAddressTitleType,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.AlternativeAddressTitleType,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.AlternativeAddressTitleType
    ],
  );

export const selectCanUploadMdoBasketRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.UploadMDOBasketEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.UploadMDOBasketEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.UploadMDOBasketEnabled
  ],
);

export const selectHasHeaderLevelCustomOrderReferenceRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.HeaderLevelCustomOrderReference,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.HeaderLevelCustomOrderReference,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.HeaderLevelCustomOrderReference
    ],
  );

export const selectRemoveAllBackorderItemsEnabledRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.RemoveAllBackorderItemsEnabled,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.RemoveAllBackorderItemsEnabled,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.RemoveAllBackorderItemsEnabled
    ],
  );

export const selectIsShippingOptionEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ShippingOptionEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ShippingOptionEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.ShippingOptionEnabled
  ],
);

export const selectShippingOptionUsePreferredRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ShippingOptionUsePreferred,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ShippingOptionUsePreferred,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.ShippingOptionUsePreferred
    ],
  );

export const selectIsBasketDeletionEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.EnableBasketDeletion,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.EnableBasketDeletion,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.EnableBasketDeletion
  ],
);

export const selectSortingPerGroupRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.SortingPerGroup,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.SortingPerGroup,
    basketConfigurationsDefaultValues[CBasketConfigurationKeys.SortingPerGroup],
  ),
  (sortingPerGroup) => JSON.parse(sortingPerGroup as string),
);

export const selectDefaultSortingRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.DefaultSorting,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.DefaultSorting,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.DefaultSorting],
);

export const selectMaxOrderRefLengthRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.MaxOrderRefLength,
  CBasketConfigurations
>(CBASKET_NAMESPACE, CBasketConfigurationKeys.MaxOrderRefLength, 20);

export const selectBasketHeaderInfoRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.BasketHeaderInfo,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.BasketHeaderInfo,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.BasketHeaderInfo
    ],
  ),
  (value) => {
    try {
      return JSON.parse(value as string);
    } catch (e) {
      return basketConfigurationsDefaultValues[
        CBasketConfigurationKeys.BasketHeaderInfo
      ];
    }
  },
);

export const selectActionsRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.Actions,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.Actions,
    basketConfigurationsDefaultValues[CBasketConfigurationKeys.Actions],
  ),
  (configValue) => {
    let actions: CBasketConfigurations[CBasketConfigurationKeys.Actions];

    try {
      actions = JSON.parse((configValue as IBasketAction[]).toString());
    } catch (e) {
      actions = [];
    }

    return actions;
  },
);

export const selectPoNumberRequiredRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.PoNumberRequired,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.PoNumberRequired,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.PoNumberRequired],
);

export const selectRestrictMixedBasketDropShipRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.RestrictMixedBasketDropShip,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.RestrictMixedBasketDropShip,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.RestrictMixedBasketDropShip
    ],
  );

export const selectShipInFullEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ShipInFullEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ShipInFullEnabled,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.ShipInFullEnabled],
);

export const selectParkingOptionsEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ParkingOptionsEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ParkingOptionsEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.ParkingOptionsEnabled
  ],
);

export const selectAllowedMixedBasketRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.AllowedMixedBasket,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.AllowedMixedBasket,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.AllowedMixedBasket
  ],
);

export const selectCopyProductsAllowedOrderTypesRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.CopyProductsAllowedOrderTypes,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.CopyProductsAllowedOrderTypes,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.CopyProductsAllowedOrderTypes
    ],
  ),
  (orderTypes) =>
    (orderTypes as OrderType[]).map((orderType) =>
      orderType.toUpperCase(),
    ) as OrderType[],
);

export const selectColumnsRuleRaw = selectConfigurationValueFrom<
  CBasketConfigurationKeys.BasketColumns,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.BasketColumns,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.BasketColumns],
);

export const selectColumnsRule = createSelector(
  selectColumnsRuleRaw,
  (basketColumnsRuleRaw) =>
    basketColumnsRuleToConfiguration(basketColumnsRuleRaw),
);

function basketColumnsRuleToConfiguration(
  basketColumnsRuleRaw?: string,
): BasketColumnsConfiguration {
  if (basketColumnsRuleRaw === undefined) {
    return basketColumnsNotConfiguredValue;
  }

  try {
    return JSON.parse(basketColumnsRuleRaw) as BasketColumnsConfiguration;
  } catch (e) {
    return basketColumnsNotConfiguredValue;
  }
}

export const defaultMaxDiscountPercentage: MaxDiscountPercentageValue = {
  [OrderType.Now]: 0,
  [OrderType.Future]: 0,
};

export const selectMaxDiscountPercentage = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.MaxDiscountPercentage,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.MaxDiscountPercentage,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.MaxDiscountPercentage
    ],
  ),
  (maxDiscountPercentage) => {
    if (maxDiscountPercentage === undefined || maxDiscountPercentage === null) {
      return defaultMaxDiscountPercentage;
    }

    const originalMaxDiscountPercentageValue = maxDiscountPercentage;

    let maxDiscountPercentageValue: MaxDiscountPercentageValue;
    let discountPercentageWasChanged = false;
    if (
      typeof maxDiscountPercentage === 'number' ||
      !isNaN(Number(maxDiscountPercentage))
    ) {
      const { discountPercentage, hasChanged } =
        getDiscountPercentageInCorrectRange(Number(maxDiscountPercentage));
      discountPercentageWasChanged = hasChanged;
      maxDiscountPercentageValue = {
        FUTURE: discountPercentage,
        NOW: discountPercentage,
      };
    } else {
      try {
        const obj = JSON.parse(
          maxDiscountPercentage.toUpperCase(),
        ) as Partial<MaxDiscountPercentageValue>;

        const futureDiscountPercentage = getDiscountPercentageInCorrectRange(
          obj[OrderType.Future] ?? 0,
        );
        discountPercentageWasChanged =
          discountPercentageWasChanged || futureDiscountPercentage.hasChanged;

        const nowDiscountPercentage = getDiscountPercentageInCorrectRange(
          obj[OrderType.Now] ?? 0,
        );
        discountPercentageWasChanged =
          discountPercentageWasChanged || nowDiscountPercentage.hasChanged;

        maxDiscountPercentageValue = {
          FUTURE: futureDiscountPercentage.discountPercentage,
          NOW: nowDiscountPercentage.discountPercentage,
        };
      } catch (error) {
        console.warn(
          `Failed to parse value of rule '${CBasketConfigurationKeys.MaxDiscountPercentage}' because ${error.message}, no discounts can be applied.`,
        );

        maxDiscountPercentageValue = defaultMaxDiscountPercentage;
      }
    }

    if (discountPercentageWasChanged) {
      console.warn(
        `The rule '${CBasketConfigurationKeys.MaxDiscountPercentage}' in namespace '${CBASKET_NAMESPACE}' with value '${originalMaxDiscountPercentageValue}' violates the range of 0.00 and 1.00.`,
      );
    }

    return maxDiscountPercentageValue;
  },
);

export const selectCanApplyManualDiscounts = createSelector(
  selectColumnsRule,
  selectMaxDiscountPercentage,
  (columnsRule, maxDiscountPercentage) => {
    const threshold = 0.01;
    return (
      Boolean(columnsRule.canApplyManualDiscount) &&
      (maxDiscountPercentage.NOW >= threshold ||
        maxDiscountPercentage.FUTURE >= threshold)
    );
  },
);

export const selectAddressLineActionsRule = createSelector(
  selectConfigurationsState,
  (config) => {
    let ADDRESS_ACTIONS_RULES_MAP: IAddressActionRulesMap;
    ADDRESS_ACTIONS_RULES_MAP = {
      [IAddressLineActions.COPY_PRODUCTS]:
        CBasketConfigurationKeys.CopyProductsEnabled,
    };

    return Object.values(IAddressLineActions).filter((action) =>
      getValueFromConfigState<IAddressLineActionRules, CBasketConfigurations>(
        config,
        CBASKET_NAMESPACE,
        ADDRESS_ACTIONS_RULES_MAP[action],
        false,
      ),
    );
  },
);

export const selectRemoveErrorItemsEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.RemoveErrorItemsEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.RemoveErrorItemsEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.RemoveErrorItemsEnabled
  ],
);

export const selectAuxGroupingFnRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.AuxGroupingFn,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.AuxGroupingFn,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.AuxGroupingFn],
);

export const selectDefaultGroupingTypeRule = createSelector(
  selectConfigurationsState,
  (config) =>
    getValueFromConfigState<
      PulseConfigurationKeys.DefaultCBasketGrouping,
      PulseConfigurations
    >(
      config,
      PULSE_FEATURE_NAME,
      PulseConfigurationKeys.DefaultCBasketGrouping,
      BasketRowsGroupType.Article,
    ),
);

export const selectProductTitleLevelRule = createSelector(
  selectConfigurationsState,
  (config) =>
    getValueFromConfigState<
      PulseConfigurationKeys.ProductTitleLevel,
      PulseConfigurations
    >(
      config,
      PULSE_FEATURE_NAME,
      PulseConfigurationKeys.ProductTitleLevel,
      basketDto.ProductTypeString.Material,
    ),
);

export const selectAddressLineTitleLevelRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.AddressLineTitleProperties,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.AddressLineTitleProperties,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.AddressLineTitleProperties
  ],
);

export const selectPreselectPropertyBagRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.PreselectPropertyBag,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.PreselectPropertyBag,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.PreselectPropertyBag
  ],
);

export const selectGroupByOptionsRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.GroupByOptions,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.GroupByOptions,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.GroupByOptions],
);

export const selectBasketPollingRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.BasketPolling,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.BasketPolling,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.BasketPolling],
);

export const selectReuseDuringEmailEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ReuseDuringEmail,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ReuseDuringEmail,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.ReuseDuringEmail],
);

export const selectCopyNowBasketEnabledRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.CopyNowBasketEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.CopyNowBasketEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.CopyNowBasketEnabled
  ],
);

export const selectBasketUploadSkuLimitRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.BasketUploadSkuLimit,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.BasketUploadSkuLimit,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.BasketUploadSkuLimit
  ],
);

export const selectBasketUploadRegardlessPresetRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.BasketUploadRegardlessPreset,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.BasketUploadRegardlessPreset,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.BasketUploadRegardlessPreset
    ],
  );

export const selectShowIdInArticleGroup = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ShowIdInArticleGroup,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ShowIdInArticleGroup,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.ShowIdInArticleGroup
  ],
);

export const selectsShowDeliveryLinePrefixTitleRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ShowDeliveryLinePrefixTitle,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ShowDeliveryLinePrefixTitle,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.ShowDeliveryLinePrefixTitle
    ],
  ),
  Boolean,
);

export const selectShowBasketNotesRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ShowBasketNotes,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ShowBasketNotes,
    basketConfigurationsDefaultValues[CBasketConfigurationKeys.ShowBasketNotes],
  ),
  Boolean,
);

export const selectColumnsShowRequestedOnPlaced = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ShowRequestedOnPlacedColumns,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ShowRequestedOnPlacedColumns,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.ShowRequestedOnPlacedColumns
  ],
);

export const selectHighlightCloseoutProducts = selectConfigurationValueFrom<
  CBasketConfigurationKeys.HighlightCloseoutProducts,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.HighlightCloseoutProducts,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.HighlightCloseoutProducts
  ],
);

export const selectUserFeedbackEnabled = selectConfigurationValueFrom<
  CBasketConfigurationKeys.UserFeedbackEnabled,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.UserFeedbackEnabled,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.UserFeedbackEnabled
  ],
);

export const selectUserFeedbackWriteEffectEnabled =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.UserFeedbackWriteEffectEnabled,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.UserFeedbackWriteEffectEnabled,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.UserFeedbackWriteEffectEnabled
    ],
  );

export const selectCheckoutAdjustMessageDelayRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.CheckoutAdjustMessageDelay,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.CheckoutAdjustMessageDelay,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.CheckoutAdjustMessageDelay
    ],
  );

export const selectManualDiscountAppliedToSelectedRule =
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ManualDiscountAppliedToSelected,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ManualDiscountAppliedToSelected,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.ManualDiscountAppliedToSelected
    ],
  );

export const selectManualDiscountsRulesString = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ManualDiscounts,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ManualDiscounts,
  basketConfigurationsDefaultValues[CBasketConfigurationKeys.ManualDiscounts],
);

export const selectShowButtonsListRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.ShowButtonsRowInBasket,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.ShowButtonsRowInBasket,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.ShowButtonsRowInBasket
  ],
);

export const selectManualDiscountsRules = createSelector(
  selectManualDiscountsRulesString,
  (manualDiscountsRulesString) => {
    try {
      const partialRules = JSON.parse(
        manualDiscountsRulesString as string,
      ) as Subset<ManualDiscountsRules>;

      const manualDiscountsRules = mergeDeep(
        defaultManualDiscountsRules,
        partialRules,
        { overrideArrays: true },
      ) as ManualDiscountsRules;

      return manualDiscountsRules;
    } catch (e) {
      console.warn(`Parsing rule for manual discounts failed, 
        falling back to default hardcoded value instead. 
        Value of rule '${manualDiscountsRulesString}'. Exception message: ${e.message}}`);
      return defaultManualDiscountsRules;
    }
  },
);

export const selectManualDiscountValidatonRuleAllowValueBelowCDiscount =
  createSelector(selectManualDiscountsRules, (manualDiscountsRules) => {
    return manualDiscountsRules.validations.allowValueBelowCDiscount;
  });

export const selectManualDiscountValidatonRuleAllowedItems = createSelector(
  selectManualDiscountsRules,
  (manualDiscountsRules) => {
    return manualDiscountsRules.allowed;
  },
);

export const selectShowBasketManualDiscountInputForGroupTypes = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes
    ],
  ),
  (valueFromConfig) => {
    const configValue = ensureArray(valueFromConfig).filter((cf) => {
      const isOfCorrectType = isString(cf);
      if (!isOfCorrectType) {
        console.warn(
          `Value '${cf}' is not a correct value for config key ${CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes}`,
        );
      }
      return isOfCorrectType;
    }) as string[];

    if (configValue.length === 0) {
      return configValue;
    }

    const allBasketRowGroupTypes = Object.values(BasketRowsGroupType).map(
      (groupType) => groupType.toUpperCase(),
    );

    const validGroupTypes: BasketRowsGroupType[] = [];

    configValue.forEach((groupTypeFromConfig) => {
      const groupType = allBasketRowGroupTypes.find(
        (gt) => gt === groupTypeFromConfig.toUpperCase(),
      );

      if (groupType) {
        validGroupTypes.push(groupType as BasketRowsGroupType);
      } else {
        console.warn(
          `BasketRowsGroupType value '${groupTypeFromConfig}' is invalid in rule '${CBasketConfigurationKeys.ShowBasketManualDiscountInputForGroupTypes}' and is therefore skipped.`,
        );
      }
    });

    return validGroupTypes;
  },
);

export const selectUserRolesAllowedToSeeManualDiscountsInNewishBasket =
  createSelector(
    selectConfigurationValueFrom<
      CBasketConfigurationKeys.UserRolesAllowedToSeeManualDiscountsInNewishBasket,
      CBasketConfigurations
    >(
      CBASKET_NAMESPACE,
      CBasketConfigurationKeys.UserRolesAllowedToSeeManualDiscountsInNewishBasket,
      basketConfigurationsDefaultValues[
        CBasketConfigurationKeys
          .UserRolesAllowedToSeeManualDiscountsInNewishBasket
      ],
    ),
    (configValue) => {
      const userRoles: UserRole[] = [];

      const allUserRoles = Object.values(UserRole);

      const configValues = ensureArray(configValue);
      configValues.forEach((valueConfigured) => {
        let userRole: UserRole | undefined;

        if (isString(valueConfigured)) {
          userRole = allUserRoles.find(
            (ur) => ur.toLowerCase() === valueConfigured.toLowerCase(),
          );
        }

        if (userRole) {
          userRoles.push(userRole);
        } else {
          console.warn(
            `UserRole value '${valueConfigured}' is invalid in rule '${CBasketConfigurationKeys.UserRolesAllowedToSeeManualDiscountsInNewishBasket}' and is therefore skipped.`,
          );
        }
      });

      if (userRoles.length === 0) {
        userRoles.push(UserRole.CustomerService);
      }

      return new Array(...new Set(userRoles)).sort();
    },
  );

export const selectShowDropShipNote = createSelector(
  selectConfigurationsState,
  (config) => {
    return getValueFromConfigState<
      CBasketConfigurationKeys.ShowDropShipNote,
      CBasketConfigurations
    >(
      config,
      CBASKET_NAMESPACE,
      CBasketConfigurationKeys.ShowDropShipNote,
      basketConfigurationsDefaultValues[
        CBasketConfigurationKeys.ShowDropShipNote
      ],
    );
  },
);

export const selectPropertyBagDropdownsRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.PropertyBagDropdowns,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.PropertyBagDropdowns,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.PropertyBagDropdowns
    ],
  ),
  (configValue): Filter[] => {
    let dropdowns: CBasketConfigurations[CBasketConfigurationKeys.PropertyBagDropdowns];

    try {
      dropdowns = JSON.parse((configValue as Filter[]).toString());
    } catch (e) {
      dropdowns = [];
    }

    return dropdowns;
  },
);

export const selectBasketValidatorFunctionsRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.ValidatorBasketFunctions,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.ValidatorBasketFunctions,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.ValidatorBasketFunctions
    ],
  ),
  (configValue): string[] => {
    try {
      return JSON.parse(configValue ?? '[]');
    } catch (e) {
      console.warn(
        'Failed to parse basket validation functions from the rule ',
        configValue,
      );
      return [];
    }
  },
);

export const selectCheckEveryGrossPriceForManualDiscountRule = createSelector(
  selectConfigurationsState,
  (config) => {
    return getValueFromConfigState<
      CBasketConfigurationKeys.CheckEveryGrossPriceForManualDiscount,
      CBasketConfigurations
    >(
      config,
      CBASKET_NAMESPACE,
      CBasketConfigurationKeys.CheckEveryGrossPriceForManualDiscount,
      basketConfigurationsDefaultValues[
        CBasketConfigurationKeys.CheckEveryGrossPriceForManualDiscount
      ],
    );
  },
);

export const selectOrderSuccessThanksAccountName = selectConfigurationValueFrom<
  CBasketConfigurationKeys.OrderSuccessThanksAccountName,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.OrderSuccessThanksAccountName,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.OrderSuccessThanksAccountName
  ],
);

export const selectDisableEditBasketOnReviewRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.DisableEditBasketOnReview,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.DisableEditBasketOnReview,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.DisableEditBasketOnReview
  ],
);

export const selectPendingBasketsAlertRule = createSelector(
  selectConfigurationValueFrom<
    CBasketConfigurationKeys.PendingBasketsAlert,
    CBasketConfigurations
  >(
    CBASKET_NAMESPACE,
    CBasketConfigurationKeys.PendingBasketsAlert,
    basketConfigurationsDefaultValues[
      CBasketConfigurationKeys.PendingBasketsAlert
    ],
  ),
  (config: string) => {
    try {
      return JSON.parse(config);
    } catch {
      console.warn('Failed to parse pending baskets alert config', config);
      return undefined;
    }
  },
);

export const selectDropShipConfirmTextRule = selectConfigurationValueFrom<
  CBasketConfigurationKeys.DropShipConfirmText,
  CBasketConfigurations
>(
  CBASKET_NAMESPACE,
  CBasketConfigurationKeys.DropShipConfirmText,
  basketConfigurationsDefaultValues[
    CBasketConfigurationKeys.DropShipConfirmText
  ],
);
